import { Recapt } from '../engine';
import { BaseEvent, BaseModule } from './base';
import { Namespace } from './types';

interface PerformanceEventDetail {
  url?: string;
  duration?: number;
  name?: string;
  attribution?: string;
  transferSize?: number;
  ttfb?: number;
  domContentLoaded?: number;
  loadTime?: number;
  connectionType?: string;
  effectiveType?: string;
  initiatorType?: string;
  status?: number;
  startTime?: number;
}

interface PerformanceEvent extends BaseEvent {
  metric: string;
  value: number;
  detail?: PerformanceEventDetail;
}

const LONG_TASK_THRESHOLD_MS = 100;
const SLOW_RESOURCE_THRESHOLD_MS = 1000;
const INP_DURATION_THRESHOLD = 40;
const API_REQUESTS_PER_SYNC_CAP = 100;

export const TRACKING_URL_PATTERNS = [
  '/v1beta/sync',
  '/collect',
  '/analytics',
  '/gtag/',
  '/gtm.js',
  'google-analytics',
  'googletagmanager',
  'hotjar',
  'fullstory',
  'sentry',
  'segment',
];

/**
 * Safely try to get a CSS selector for an element.
 * Returns null if anything goes wrong — never throws.
 */
function safeSelector(node: unknown): string | null {
  try {
    if (!node || typeof node !== 'object') return null;
    const el = node as Element;
    if (!el.tagName) return null;
    const tag = el.tagName.toLowerCase();
    const id = el.id ? `#${el.id}` : '';
    const cls =
      el.className && typeof el.className === 'string'
        ? '.' + el.className.trim().split(/\s+/).slice(0, 2).join('.')
        : '';
    return `${tag}${id}${cls}`.slice(0, 120) || null;
  } catch {
    return null;
  }
}

/**
 * Passively collects Web Vitals and performance metrics using the
 * browser-native PerformanceObserver API.
 *
 * Safety guarantees:
 * - Every observer and callback is wrapped in try/catch
 * - Feature-detected — graceful no-op if PerformanceObserver is unavailable
 * - Zero monkey-patching — uses only passive observation
 * - No polling, no requestAnimationFrame, no setInterval
 */
export class PerformanceModule extends BaseModule<PerformanceEvent> {
  public namespace: Namespace = 'performance';

  private observers: PerformanceObserver[] = [];
  private clsAccumulator = 0;
  private navCollected = false;
  private apiRequestCount = 0;
  private visibilityHandler: (() => void) | null = null;

  onStart() {
    try {
      if (typeof PerformanceObserver === 'undefined') return;

      this.observeLcp();
      this.observeCls();
      this.observeInp();
      this.observeLongTasks();
      this.observeSlowResources();
      this.observeApiRequests();
      this.collectNavigationTiming();
      this.collectConnectionInfo();
      this.observeVisibility();
    } catch {
      /* never throw on customer site */
    }
  }

  onStop() {
    for (const obs of this.observers) {
      try {
        obs.disconnect();
      } catch {
        /* */
      }
    }
    this.observers = [];
    if (this.visibilityHandler) {
      try {
        document.removeEventListener(
          'visibilitychange',
          this.visibilityHandler,
        );
      } catch {
        /* */
      }
      this.visibilityHandler = null;
    }
  }

  preBuildRequest() {
    this.flushCls();
    const state = this.state.slice();
    this.state = [];
    this.apiRequestCount = 0;
    return state;
  }

  purgeState(): void {
    this.state = [];
    this.clsAccumulator = 0;
    this.apiRequestCount = 0;
  }

  buildRequest(
    state: PerformanceEvent[],
  ): Record<string, unknown> & { state: PerformanceEvent[] } {
    return { state };
  }

  // -- Observers --------------------------------------------------------

  private createObserver(
    type: string,
    callback: (entries: PerformanceEntryList) => void,
    options?: Record<string, unknown>,
  ): void {
    try {
      if (!PerformanceObserver.supportedEntryTypes?.includes(type)) return;

      const obs = new PerformanceObserver((list) => {
        try {
          callback(list.getEntries());
        } catch {
          /* */
        }
      });
      obs.observe({
        type,
        buffered: true,
        ...options,
      } as PerformanceObserverInit);
      this.observers.push(obs);
    } catch {
      /* unsupported entry type — graceful no-op */
    }
  }

  private push(metric: string, value: number, detail?: PerformanceEventDetail) {
    if (!this.isReady) return;
    const evt: PerformanceEvent = {
      timestamp: Recapt.getCurrentSessionTimestamp(),
      where: Recapt.where(),
      metric,
      value: +value.toFixed(1),
    };
    if (detail) evt.detail = detail;
    this.state.push(evt);
  }

  private observeLcp() {
    this.createObserver('largest-contentful-paint', (entries) => {
      const last = entries[entries.length - 1];
      if (!last) return;
      this.push('lcp', last.startTime);
    });
  }

  private observeCls() {
    this.createObserver('layout-shift', (entries) => {
      for (const entry of entries) {
        const ls = entry as PerformanceEntry & {
          hadRecentInput?: boolean;
          value?: number;
        };
        if (ls.hadRecentInput) continue;
        this.clsAccumulator += ls.value ?? 0;
      }
    });
  }

  /** Emit accumulated CLS before each sync. */
  private flushCls() {
    if (this.clsAccumulator > 0) {
      this.push('cls', this.clsAccumulator);
      this.clsAccumulator = 0;
    }
  }

  private observeInp() {
    this.createObserver(
      'event',
      (entries) => {
        for (const entry of entries) {
          const dur = entry.duration;
          if (dur <= INP_DURATION_THRESHOLD) continue;
          const detail: PerformanceEventDetail = {};
          const evt = entry as PerformanceEntry & { target?: Element };
          const sel = safeSelector(evt.target);
          if (sel) detail.attribution = sel;
          this.push(
            'inp',
            dur,
            Object.keys(detail).length > 0 ? detail : undefined,
          );
        }
      },
      { durationThreshold: INP_DURATION_THRESHOLD },
    );
  }

  private observeLongTasks() {
    this.createObserver('longtask', (entries) => {
      for (const entry of entries) {
        if (entry.duration < LONG_TASK_THRESHOLD_MS) continue;
        this.push('longtask', entry.duration);
      }
    });
  }

  private observeSlowResources() {
    this.createObserver('resource', (entries) => {
      for (const entry of entries) {
        if (entry.duration < SLOW_RESOURCE_THRESHOLD_MS) continue;
        const res = entry as PerformanceResourceTiming;
        const name = res.name;
        let url: string | undefined;
        try {
          const u = new URL(name);
          url = u.pathname.slice(0, 200);
        } catch {
          url = name.slice(0, 200);
        }
        this.push('slow_resource', entry.duration, {
          url,
          transferSize: res.transferSize,
        });
      }
    });
  }

  private observeApiRequests() {
    this.createObserver('resource', (entries) => {
      for (const entry of entries) {
        if (this.apiRequestCount >= API_REQUESTS_PER_SYNC_CAP) break;

        const res = entry as PerformanceResourceTiming;
        const initiator = res.initiatorType;
        if (initiator !== 'xmlhttprequest' && initiator !== 'fetch') continue;

        const name = res.name;
        let pathname: string | undefined;
        try {
          const u = new URL(name);
          if (TRACKING_URL_PATTERNS.some((p) => u.href.includes(p))) continue;
          pathname = u.pathname.slice(0, 200);
        } catch {
          pathname = name.slice(0, 200);
        }

        const status =
          (res as PerformanceResourceTiming & { responseStatus?: number })
            .responseStatus ?? 0;

        this.push('api_request', +entry.duration.toFixed(1), {
          url: pathname,
          initiatorType: initiator,
          status,
          transferSize: res.transferSize,
          startTime: +res.startTime.toFixed(1),
        });
        this.apiRequestCount++;
      }
    });
  }

  private observeVisibility() {
    try {
      if (typeof document === 'undefined') return;
      this.visibilityHandler = () => {
        try {
          this.push(
            'visibility_change',
            document.visibilityState === 'visible' ? 1 : 0,
          );
        } catch {
          /* */
        }
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
    } catch {
      /* visibilitychange unavailable */
    }
  }

  // -- One-time reads ---------------------------------------------------

  private collectNavigationTiming() {
    if (this.navCollected) return;
    try {
      const navEntries = performance.getEntriesByType('navigation');
      if (!navEntries.length) return;
      const nav = navEntries[0] as PerformanceNavigationTiming;
      this.navCollected = true;
      this.push(
        'navigation',
        nav.loadEventEnd || nav.domContentLoadedEventEnd,
        {
          ttfb: +nav.responseStart.toFixed(1),
          domContentLoaded: +nav.domContentLoadedEventEnd.toFixed(1),
          loadTime: +(nav.loadEventEnd || nav.domContentLoadedEventEnd).toFixed(
            1,
          ),
        },
      );
    } catch {
      /* navigation timing unavailable */
    }
  }

  private collectConnectionInfo() {
    try {
      const conn = (
        navigator as Navigator & {
          connection?: { type?: string; effectiveType?: string };
        }
      ).connection;
      if (!conn) return;
      this.push('connection', 0, {
        connectionType: conn.type,
        effectiveType: conn.effectiveType,
      });
    } catch {
      /* connection API unavailable */
    }
  }
}
